import { AxiosInstance } from 'axios';
import Runner, { FlowRun, Options, FlowRunEvents } from '@ply-io/runner';
import { WorkflowHashedId } from '@ply-io/ply-front-shared-utilities';
import {
	FlowRunDoneCallback,
	FlowRunFailedCallback,
	FlowRunFinalizedCallback,
	FlowRunFoundWaitingNodeRunCallback,
	FlowRunInput,
	FlowRunNodeRunDoneCallback,
	FlowRunNodeRunUpdateCallback,
	FlowRunRenderableWidgetCallback,
	FlowRunStateChangeCallback,
	FlowRunStatusChangeCallback,
} from '@ply-io/runner';

const { VUE_APP_PUSH_AUTH_ENDPOINT } = process.env;

export interface FlowRunListeners {
	[FlowRunEvents.StateChange]: FlowRunStateChangeCallback;
	[FlowRunEvents.StatusChange]: FlowRunStatusChangeCallback;
	[FlowRunEvents.Failed]: FlowRunFailedCallback;
	[FlowRunEvents.Done]: FlowRunDoneCallback;
	[FlowRunEvents.NodeRunUpdate]: FlowRunNodeRunUpdateCallback;
	[FlowRunEvents.NodeRunDone]: FlowRunNodeRunDoneCallback;
	[FlowRunEvents.FoundWaitingNodeRun]: FlowRunFoundWaitingNodeRunCallback;
	[FlowRunEvents.RenderableWidget]: FlowRunRenderableWidgetCallback;
	[FlowRunEvents.Finalized]: FlowRunFinalizedCallback;
}

const registerFlowRunEvents = (flowRun: FlowRun, listeners: Partial<FlowRunListeners>) => {
	listeners[FlowRunEvents.StateChange] && flowRun.onStateChange(listeners[FlowRunEvents.StateChange] as FlowRunStateChangeCallback);
	listeners[FlowRunEvents.StatusChange] && flowRun.onStatusChange(listeners[FlowRunEvents.StatusChange] as FlowRunStatusChangeCallback);
	listeners[FlowRunEvents.Done] && flowRun.onDone(listeners[FlowRunEvents.Done] as FlowRunDoneCallback);
	listeners[FlowRunEvents.Failed] && flowRun.onFailed(listeners[FlowRunEvents.Failed] as FlowRunFailedCallback);
	listeners[FlowRunEvents.FoundWaitingNodeRun] &&
		flowRun.onFoundWaitingNodeRun(listeners[FlowRunEvents.FoundWaitingNodeRun] as FlowRunFoundWaitingNodeRunCallback);
	listeners[FlowRunEvents.NodeRunDone] && flowRun.onNodeRunDone(listeners[FlowRunEvents.NodeRunDone] as FlowRunNodeRunDoneCallback);
	listeners[FlowRunEvents.NodeRunUpdate] && flowRun.onNodeRunUpdate(listeners[FlowRunEvents.NodeRunUpdate] as FlowRunNodeRunUpdateCallback);
	listeners[FlowRunEvents.RenderableWidget] &&
		flowRun.onRenderableWidget(listeners[FlowRunEvents.RenderableWidget] as FlowRunRenderableWidgetCallback);
	listeners[FlowRunEvents.Finalized] && flowRun.onFinalized(listeners[FlowRunEvents.Finalized] as FlowRunFinalizedCallback);
};

/**
 *
 * @param runner
 * @param hashId
 * @param input
 * @param listeners
 * @returns
 */
const runWorkflow: (
	runner: Runner,
	hashId: WorkflowHashedId,
	input: Partial<FlowRunInput>,
	listeners: Partial<FlowRunListeners>
) => Promise<FlowRun> = async (runner, hashId, input, listeners) => {
	// const input: FlowRunInput = { mappedFields, updatesId: '' };
	const flowRun = await runner.initFlow(hashId, { ...input, updatesId: '' } as FlowRunInput);
	registerFlowRunEvents(flowRun, listeners);
	return flowRun;
};

export function useRunner(runnerApi: AxiosInstance) {
	const runner: Runner = new Runner(runnerApi, {
		pushAuthEndpoint: VUE_APP_PUSH_AUTH_ENDPOINT,
	} as Options);

	return {
		runWorkflow: (hashId: WorkflowHashedId, input: Partial<FlowRunInput>, listeners: Partial<FlowRunListeners>) =>
			runWorkflow(runner, hashId, input, listeners),
	};
}
